import * as Sentry from '@sentry/react';

import { HOUR_MS } from '../constants';

import { clientVersionHeaders } from './clientVersion';
import { getAuthToken, hasAcquiredTokenThisLoad } from './getAuthToken';

import type { AuthTokenGetter } from './getAuthToken';

/**
 * Single source of truth for the `name` of every auth error this module defines.
 *
 * The names are load-bearing: downstream seams match on the STRING, not on
 * `instanceof`, because the RTK Query base query flattens a thrown error into a
 * plain `{ error, name }` payload long before the middleware sees it. Deriving
 * both the classes' `name` and the matcher sets from this one object is what
 * makes a rename a compile error instead of a silently dead recovery path.
 */
export const AUTH_ERROR_NAME = {
  tokenUnavailable: 'AuthTokenUnavailableError',
  sessionExpired: 'SessionExpiredError',
  sessionLost: 'SessionLostError',
  accessDenied: 'AccessDeniedError',
  accessRefused: 'AccessRefusedError',
} as const;

/** The only status the backend uses for "this session is not authenticated". */
export type AuthFailureStatus = 401;

/**
 * No token could be acquired, and none ever was on this page load: the normal
 * pre-login state. `AuthProvider` owns the sign-in flow for this one, so the
 * recovery seams deliberately do NOT react to it.
 */
export class AuthTokenUnavailableError extends Error {
  override readonly name = AUTH_ERROR_NAME.tokenUnavailable;

  constructor() {
    super('AUTH ERROR. Failed to acquire token.');
  }
}

/**
 * A token existed and the backend rejected it with a 401 that a forced refresh
 * could not recover. Surfacing a typed error (instead of a JSON parse error on
 * the response body) is what lets the app re-authenticate rather than sit on a
 * silently frozen board.
 */
export class SessionExpiredError extends Error {
  override readonly name = AUTH_ERROR_NAME.sessionExpired;

  readonly status: AuthFailureStatus;

  constructor(status: AuthFailureStatus) {
    super(`AUTH ERROR. Session expired (HTTP ${status}).`);
    this.status = status;
  }
}

/**
 * A token was acquired earlier on this page load and has now vanished, so there
 * is no request to get a 401 from. Amplify produces exactly this when the
 * REFRESH token expires: `fetchAuthSession` resolves with no tokens rather than
 * throwing, so without this case a long-lived board freezes with no 401 to
 * trigger anything. Distinct from `AuthTokenUnavailableError` only by whether a
 * token was ever held — which is the whole difference between "not logged in
 * yet" and "logged out under us".
 */
export class SessionLostError extends Error {
  override readonly name = AUTH_ERROR_NAME.sessionLost;

  constructor() {
    super('AUTH ERROR. Session lost mid-session.');
  }
}

/**
 * HTTP 403: authenticated, but not a member of the group the endpoint requires.
 * NOT a session failure — the backend's `RolesGuard` raises it from a PG-RBAC
 * membership check, so no token refresh and no re-authentication can ever change
 * the answer. Kept separate from `SessionExpiredError` precisely so it cannot
 * drive a refresh or a redirect loop.
 */
export class AccessDeniedError extends Error {
  override readonly name = AUTH_ERROR_NAME.accessDenied;

  readonly status = 403;

  constructor() {
    super('AUTH ERROR. Access denied (HTTP 403).');
  }
}

/**
 * HTTP 403 from a route whose refusal is a DESIGNED answer rather than a verdict
 * about the user — the admin events routes refuse on any service that is not a
 * developer's own, and say why in the body.
 *
 * DO NOT COLLAPSE THIS INTO `AccessDeniedError`. Every recovery seam matches on
 * the NAME, so sharing one would make `handleAccessDenied` clear the
 * granted-groups cache and hard-navigate to `/403` the moment the Events tab asks
 * for its catalogue: the refusal Alert could never render, and an unrelated cache
 * would go with it. Nothing reacts to this name; the caller renders it.
 *
 * Its `message` is the server's sentence VERBATIM where it sent one, because the
 * base query flattens a thrown error to `{ error: message, name }` and the message
 * is the only part that reaches the UI.
 */
export class AccessRefusedError extends Error {
  override readonly name = AUTH_ERROR_NAME.accessRefused;

  readonly status = 403;

  constructor(detail: string | null) {
    super(detail ?? 'The server refused this request (HTTP 403).');
  }
}

/**
 * Errors no generic capture point may report. Either they carry their own
 * dedicated auth telemetry, emitted here or (for `token_missing`) by
 * `fetchWithAuthHeaders` — one session loss would otherwise be reported once per
 * failing endpoint — or, for `accessRefused`, they are a designed answer and not a
 * failure at all.
 */
const REPORTED_AUTH_ERROR_NAMES: ReadonlySet<string> = new Set(
  Object.values(AUTH_ERROR_NAME),
);

/** Errors that mean the session itself is gone, so re-authentication can help. */
const SESSION_LOST_ERROR_NAMES: ReadonlySet<string> = new Set([
  AUTH_ERROR_NAME.sessionExpired,
  AUTH_ERROR_NAME.sessionLost,
]);

const errorName = (error: unknown): string => {
  const name = (error as { name?: unknown } | null | undefined)?.name;
  return typeof name === 'string' ? name : '';
};

export const isReportedAuthError = (error: unknown): boolean =>
  REPORTED_AUTH_ERROR_NAMES.has(errorName(error));

export const isSessionLostError = (error: unknown): boolean =>
  SESSION_LOST_ERROR_NAMES.has(errorName(error));

export const isAccessDeniedError = (error: unknown): boolean =>
  errorName(error) === AUTH_ERROR_NAME.accessDenied;

export const isAccessRefusedError = (error: unknown): boolean =>
  errorName(error) === AUTH_ERROR_NAME.accessRefused;

/**
 * Why an auth failure happened — the actionable signal, because each reason has
 * a different owner:
 *   token_missing                — no token, none ever held (pre-login). FE, expected.
 *   session_lost                 — a held token vanished mid-session. FE/auth.
 *   refresh_failed               — the forced refresh yielded no token. FE/auth.
 *   token_rejected_after_refresh — a freshly refreshed token was still 401'd. BE/session config.
 *   access_denied                — 403, the user is not in the required group. BE RBAC / provisioning.
 *
 * A plain expiry is recovered transparently by the forced refresh in
 * `fetchWithAuthRecovery` and never appears here at all.
 */
export type AuthLoss =
  | { reason: 'token_missing' }
  | { reason: 'session_lost' }
  | { reason: 'refresh_failed'; status: AuthFailureStatus }
  | { reason: 'token_rejected_after_refresh'; status: AuthFailureStatus }
  | { reason: 'access_denied'; status: 403 };

/**
 * `token_missing` fires on the happy path — `AuthProvider` renders children
 * while it is still resolving the session, so every cold pre-login load produces
 * one. It is therefore sampled, as it was before this module existed. Every other
 * reason only occurs during a real incident, so those are unsampled: one event
 * per affected page load gives accurate incident volume.
 */
const EXPECTED_REASON_SAMPLE_RATE = 0.01;
const isExpectedReason = (reason: AuthLoss['reason']): boolean =>
  reason === 'token_missing';

/**
 * Dedupe is deliberately IN-MEMORY rather than `sessionStorage`:
 *  - it matches the documented "once per page load" intent, where sessionStorage
 *    silently survives the reload that a re-auth redirect causes, permanently
 *    suppressing every later incident in that tab;
 *  - storage can be unavailable or throw (private mode, blocked third-party
 *    contexts), which would have silenced ALL auth telemetry for the page while
 *    every other capture point kept suppressing on `isReportedAuthError`.
 */
const reportedReasons = new Set<AuthLoss['reason']>();

/** Test-only. Resets the once-per-page-load dedupe. */
export const __resetAuthReportDedupe = (): void => reportedReasons.clear();

const tabAgeBucket = (ageMs: number): string => {
  if (ageMs < HOUR_MS) {
    return '<1h';
  }
  if (ageMs < 4 * HOUR_MS) {
    return '1-4h';
  }
  if (ageMs < 8 * HOUR_MS) {
    return '4-8h';
  }
  return '>8h';
};

/**
 * Dedicated telemetry for an auth failure, emitted at most once per page load
 * per reason. Carries tab age (time since page load, NOT auth-session age — it
 * resets on reload) so a post-load degradation is distinguishable from a
 * fresh-load failure.
 *
 * @returns whether an event was actually handed to Sentry. Callers that suppress
 *   their own generic reporting on the assumption that this fired MUST honour a
 *   `false` here, or the failure reports nothing at all.
 *
 * Note this is the last unsampled step, not a delivery guarantee: `beforeSend`
 * still applies (see `app/monitoring/sentryFilters.ts`, which exempts
 * `category: 'auth'` from its rate limiter for exactly this reason).
 */
export const reportAuthLoss = (loss: AuthLoss): boolean => {
  const { reason } = loss;
  if (reportedReasons.has(reason)) {
    return false;
  }
  if (isExpectedReason(reason) && Math.random() > EXPECTED_REASON_SAMPLE_RATE) {
    // Sampled out. Still marked reported: one attempt per page load either way.
    reportedReasons.add(reason);
    return false;
  }

  const ageMs = Math.round(performance.now());
  const status = 'status' in loss ? loss.status : undefined;

  try {
    Sentry.captureMessage('auth.session_expired', {
      level: isExpectedReason(reason) ? 'warning' : 'error',
      tags: {
        category: 'auth',
        ['auth_error']: reason,
        ['tab_age_bucket']: tabAgeBucket(ageMs),
        ...(status === undefined ? {} : { ['http_status']: status }),
      },
      fingerprint: ['auth-session-expired', reason],
      extra: { ['tab_age_ms']: ageMs },
    });
  } catch {
    // Never block the auth flow on telemetry — but do NOT mark it reported, so
    // the caller learns nothing was captured and can fall back.
    return false;
  }

  reportedReasons.add(reason);
  return true;
};

/**
 * The token acquisition to use when a request has just been rejected: a single
 * forced refresh shared by every concurrent caller.
 *
 * Coalescing is required, not an optimisation. All aggregate hooks poll on one
 * aligned tick and `allSignalsBumped()` refetches them together, so an expiry
 * rejects N requests simultaneously. Amplify's `refreshTokens` does no
 * single-flighting of its own — each caller POSTs its own `REFRESH_TOKEN` grant
 * and writes the result back — so N racing refreshes against a pool with refresh
 * token rotation enabled can invalidate the very session the winner repaired.
 */
let inFlightRefresh: Promise<string | null | undefined> | null = null;

const refreshTokenOnce = (
  refreshToken: AuthTokenGetter,
): Promise<string | null | undefined> => {
  inFlightRefresh ??= refreshToken({ forceRefresh: true }).finally(() => {
    inFlightRefresh = null;
  });
  return inFlightRefresh;
};

/**
 * What an authenticated request may vary. Absent ⇒ a GET with no body, which is
 * every endpoint the app had before the events workbench needed to POST.
 */
export interface AuthedRequestOptions {
  signal?: AbortSignal;
  /** DELETE clears the activity-monitoring flag; that route takes no body. */
  method?: 'GET' | 'POST' | 'DELETE';
  /** Serialised as the JSON body. `undefined` sends no body at all. */
  json?: unknown;
  /**
   * Set on a route whose 403 is a DESIGNED refusal rather than a group denial —
   * the admin events routes refuse outside a developer's own service, and say why
   * in the body.
   *
   * The 403 then raises `AccessRefusedError` instead of `AccessDeniedError`, which
   * is what keeps every recovery seam out of it: no `access_denied` incident (whose
   * owner is "BE RBAC / provisioning", which would be a lie), no granted-groups
   * cache clear and no navigation to `/403`. The server's reason becomes the
   * error's message, so the UI shows the sentence naming the fix.
   */
  expectedForbidden?: boolean;
  /**
   * The getter the 401 path force-refreshes through; `getAuthToken` when absent. A caller
   * with an injected `getToken` passes it, so one seam covers both acquisitions. Concurrent
   * callers still share one in-flight refresh, whichever getter started it.
   */
  refreshToken?: AuthTokenGetter;
}

const authedInit = (
  token: string,
  options: AuthedRequestOptions | undefined,
): RequestInit => ({
  method: options?.method ?? 'GET',
  headers: {
    Authorization: token,
    ...clientVersionHeaders(),
    ...(options?.json === undefined
      ? {}
      : { ['Content-Type']: 'application/json' }),
  },
  ...(options?.json === undefined
    ? {}
    : { body: JSON.stringify(options.json) }),
  signal: options?.signal,
});

export const readErrorBody = async (res: Response): Promise<unknown> => {
  try {
    return JSON.parse(await res.text()) as unknown;
  } catch {
    return null;
  }
};

/**
 * The `message` out of a Nest error body, or null for anything else.
 *
 * Total, and deliberately narrow: only a recognised JSON `message` is returned, so
 * an HTML error page or a proxy's plain text can never be rendered into the UI as
 * if the server had explained itself.
 */
export const serverMessageOf = (body: unknown): string | null => {
  const message = (body as { message?: unknown } | null)?.message;
  return typeof message === 'string' && message.length > 0 ? message : null;
};

export const readServerMessage = async (
  res: Response,
): Promise<string | null> => serverMessageOf(await readErrorBody(res));

/**
 * One authenticated request, with in-place session recovery.
 *
 * A 401 body is frequently plain text ("Unauthorized") or an HTML login page, so
 * it must NEVER be parsed. On a 401 we attempt a single coalesced forced refresh
 * and retry once; if that still fails the caller gets a typed error plus
 * telemetry naming the owner, instead of a schema error on a login page. A 403 is
 * not a session problem at all (see `AccessDeniedError`) and is never refreshed;
 * with `expectedForbidden` it raises `AccessRefusedError` instead, reports no
 * incident, and DOES read its body — for the reason given on that option.
 *
 * The returned `Response` is therefore guaranteed to be neither 401 nor 403.
 * It is NOT guaranteed to be `ok` — a 5xx passes straight through, so callers
 * that parse the body still have to cope with a non-JSON error page.
 *
 * Takes the token as an argument rather than acquiring it, so a caller that
 * already holds one can reuse it across many requests: `domainEventCatchup`
 * drains up to `MAX_PAGES` pages on one token. Note the refreshed token is
 * deliberately not returned, so such a caller pays its own 401-plus-refresh once
 * per page; threading it back out is the obvious improvement if that ever shows
 * up in practice.
 */
export const fetchWithAuthRecovery = async (
  url: string,
  token: string,
  options?: AuthedRequestOptions,
): Promise<Response> => {
  let res = await fetch(url, authedInit(token, options));

  if (res.status === 403) {
    if (options?.expectedForbidden) {
      throw new AccessRefusedError(await readServerMessage(res));
    }
    reportAuthLoss({ reason: 'access_denied', status: 403 });
    throw new AccessDeniedError();
  }

  if (res.status !== 401) {
    return res;
  }

  // RTK Query already gave up on this request; spending a Cognito round-trip on
  // it would be pure waste (and the retry below could not be delivered anyway).
  if (options?.signal?.aborted) {
    return res;
  }

  const refreshedToken = await refreshTokenOnce(
    options?.refreshToken ?? getAuthToken,
  );
  if (refreshedToken) {
    // Rebuilt from `options`, so the retry carries the SAME method and body as
    // the attempt that was rejected. A write retried as a bare GET would 404 or,
    // worse, succeed against a different route and report the wrong thing.
    res = await fetch(url, authedInit(refreshedToken, options));
  }

  if (res.status === 401) {
    // Distinguish "the refresh itself failed" from "a fresh token was still
    // rejected" — different owners (FE/auth vs BE/config), same UX.
    reportAuthLoss({
      reason: refreshedToken
        ? 'token_rejected_after_refresh'
        : 'refresh_failed',
      status: 401,
    });
    throw new SessionExpiredError(401);
  }

  return res;
};

/**
 * Classifies a falsy `getAuthToken()` result at the START of a request, where
 * there is no response status to go on.
 *
 * The distinction is the whole of TMDXNC-52's residual case: Amplify returns no
 * tokens (rather than throwing) once the refresh token expires, so a board that
 * has been open for days never issues a request and never sees a 401. Treating
 * that as the pre-login state would leave it frozen forever.
 */
export const authTokenMissingError = (): Error => {
  if (hasAcquiredTokenThisLoad()) {
    reportAuthLoss({ reason: 'session_lost' });
    return new SessionLostError();
  }
  reportAuthLoss({ reason: 'token_missing' });
  return new AuthTokenUnavailableError();
};
